import { useState, useRef } from 'react'
import { Card, Input, Space, Typography, Tag, Popover, Button } from 'antd'
import { SmileOutlined, NumberOutlined } from '@ant-design/icons'

const { TextArea } = Input
const { Text } = Typography

interface CaptionEditorProps {
  value?: string
  onChange?: (value: string) => void
  selectedPlatforms?: string[]
  minCharLimit?: number // Minimum across selected platforms
}

// Common hashtags for medical/health content
const suggestedHashtags = [
  '#zdravlje',
  '#medicina',
  '#doktor',
  '#zdravstvo',
  '#beograd',
  '#srbija',
  '#zdravzivot',
  '#prevencija',
  '#lecenje',
  '#savet',
]

// Common emojis for social media
const commonEmojis = [
  '😊', '👍', '❤️', '🙏', '💪', '✨', '🎉', '👏',
  '💯', '🔥', '⭐', '💡', '📌', '🏥', '💊', '🩺',
  '❓', '✅', '➡️', '📍', '📞', '💬', '👇', '👆',
]

export function CaptionEditor({
  value = '',
  onChange,
  selectedPlatforms = [],
  minCharLimit = 2200,
}: CaptionEditorProps) {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  // Insert text at cursor position
  const insertAtCursor = (text: string) => {
    const textArea = textAreaRef.current?.resizableTextArea?.textArea
    if (!textArea) {
      onChange?.(value + text)
      return
    }

    const start = textArea.selectionStart
    const end = textArea.selectionEnd
    const newValue = value.substring(0, start) + text + value.substring(end)
    onChange?.(newValue)

    // Restore cursor position after insertion
    setTimeout(() => {
      textArea.focus()
      textArea.setSelectionRange(start + text.length, start + text.length)
    }, 0)
  }

  // Insert hashtag
  const insertHashtag = (hashtag: string) => {
    // Add space before if needed
    const prefix = value.length > 0 && !value.endsWith(' ') && !value.endsWith('\n') ? ' ' : ''
    insertAtCursor(prefix + hashtag)
  }

  // Insert emoji
  const insertEmoji = (emoji: string) => {
    insertAtCursor(emoji)
    setShowEmojiPicker(false)
  }

  // Character count color
  const getCountColor = () => {
    const percentage = value.length / minCharLimit
    if (percentage > 1) return '#ff4d4f'
    if (percentage > 0.9) return '#faad14'
    return undefined
  }

  // Check if Twitter is selected (strictest limit)
  const hasTwitter = selectedPlatforms.includes('TWITTER')

  return (
    <Card title="Opis posta" size="small">
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        {/* Caption input */}
        <div style={{ position: 'relative' }}>
          <TextArea
            ref={textAreaRef as React.RefObject<any>}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder="Napisi opis posta...&#10;&#10;Prva linija ce biti YouTube naslov."
            rows={6}
            style={{ paddingBottom: 40 }}
          />

          {/* Toolbar */}
          <div
            style={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              right: 8,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Space>
              {/* Emoji picker */}
              <Popover
                open={showEmojiPicker}
                onOpenChange={setShowEmojiPicker}
                trigger="click"
                placement="topLeft"
                content={
                  <div style={{ width: 240 }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(8, 1fr)',
                        gap: 4,
                      }}
                    >
                      {commonEmojis.map((emoji) => (
                        <Button
                          key={emoji}
                          type="text"
                          size="small"
                          onClick={() => insertEmoji(emoji)}
                          style={{ fontSize: 18, padding: 4 }}
                        >
                          {emoji}
                        </Button>
                      ))}
                    </div>
                  </div>
                }
              >
                <Button type="text" size="small" icon={<SmileOutlined />} />
              </Popover>

              {/* Hashtag suggestions */}
              <Popover
                trigger="click"
                placement="topLeft"
                content={
                  <div style={{ maxWidth: 280 }}>
                    <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
                      Popularni hashtagovi
                    </Text>
                    <Space wrap size={4}>
                      {suggestedHashtags.map((tag) => (
                        <Tag
                          key={tag}
                          style={{ cursor: 'pointer' }}
                          onClick={() => insertHashtag(tag)}
                        >
                          {tag}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                }
              >
                <Button type="text" size="small" icon={<NumberOutlined />} />
              </Popover>
            </Space>

            {/* Character count */}
            <Text style={{ color: getCountColor(), fontSize: 12 }}>
              {value.length} / {minCharLimit}
            </Text>
          </div>
        </div>

        {/* Platform-specific hints */}
        {hasTwitter && value.length > 280 && (
          <Text type="danger" style={{ fontSize: 12 }}>
            X (Twitter) ima limit od 280 karaktera. Tekst ce biti skracen.
          </Text>
        )}

        {value.length > 0 && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            💡 Prva linija teksta ce biti koriscena kao naslov za YouTube.
          </Text>
        )}

        {/* Quick hashtag buttons */}
        <div>
          <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
            Brzo dodaj:
          </Text>
          <Space wrap size={4}>
            {suggestedHashtags.slice(0, 5).map((tag) => (
              <Tag
                key={tag}
                style={{ cursor: 'pointer' }}
                onClick={() => insertHashtag(tag)}
              >
                + {tag}
              </Tag>
            ))}
          </Space>
        </div>
      </Space>
    </Card>
  )
}

export default CaptionEditor
